import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { services } from '../data/mock';

const Navbar = ({ onOpenPopup }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isLandingPage = location.pathname === '/lp';

  if (isLandingPage) {
    return null;
  }


  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img src="/assets/img/logo.png" alt="Trademark Credentials" className="h-[3.5rem] w-auto" />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link to="/" className="text-gray-700 hover:text-purple-700 font-medium transition-colors">
              Home
            </Link>
            <Link to="/about" className="text-gray-700 hover:text-purple-700 font-medium transition-colors">
              About Us
            </Link>
            
            {/* Services Mega Menu */}
            <div
              className="relative"
              onMouseEnter={() => setShowMegaMenu(true)}
              onMouseLeave={() => setShowMegaMenu(false)}
            >
              <button className="text-gray-700 hover:text-purple-700 font-medium transition-colors flex items-center">
                Services
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              
              {showMegaMenu && (
                <div className="absolute left-1/2 transform -translate-x-1/2 top-full mt-2 w-96 bg-white rounded-lg shadow-2xl border border-gray-100 p-6">
                  <div className="grid gap-3">
                    {services.map((service) => (
                      <Link
                        key={service.id}
                        to={`/services/${service.slug}`}
                        className="p-3 rounded-lg hover:bg-purple-50 transition-colors group"
                      >
                        <h3 className="font-semibold text-gray-800 group-hover:text-purple-700 transition-colors">
                          {service.title}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/contact" className="text-gray-700 hover:text-purple-700 font-medium transition-colors">
              Contact Us
            </Link>
          </div>

          {/* Right Side Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button
              onClick={onOpenPopup}
              className="bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 text-white px-6 py-2 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
            >
              Register My Trademark
            </Button>
            <Button
              
              variant="outline"
              className="js-chat border-2 border-purple-600 text-purple-700 hover:bg-purple-50 hover:text-purple-800 px-6 py-2 rounded-lg font-semibold transition-all duration-300"
            >
              <MessageCircle className="mr-2 h-4 w-4" />
              Chat Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-gray-700 hover:text-purple-400 transition-colors"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100">
            <div className="flex flex-col space-y-4">
              <Link
                to="/"
                className="text-gray-700 hover:text-purple-400 font-medium transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/about"
                className="text-gray-700 hover:text-purple-400 font-medium transition-colors"
                onClick={() => setIsOpen(false)}
              >
                About Us
              </Link>
              
              <div className="text-gray-700 font-medium">Services</div>
              <div className="pl-4 flex flex-col space-y-2">
                {services.map((service) => (
                  <Link
                    key={service.id}
                    to={`/services/${service.slug}`}
                    className="text-gray-600 hover:text-purple-400 transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    {service.title}
                  </Link>
                ))}
              </div>

              <Link
                to="/contact"
                className="text-gray-700 hover:text-purple-400 font-medium transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Contact Us
              </Link>
              
              <div className="pt-4 space-y-2">
                <Button
                  onClick={() => {
                    onOpenPopup();
                    setIsOpen(false);
                  }}
                  className="w-full bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 text-white"
                >
                  Register My Trademark
                </Button>
                <Button
                
                  variant="outline"
                  className="js-chat w-full border-2 border-purple-600 text-purple-700 hover:bg-purple-50"
                >
                  <MessageCircle className="mr-2 h-4 w-4" />
                  Chat Now
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
